import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { getPageContents } from "../../../actions/admin/cmspage";

import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import { cn } from "../../../utils/classnames";
import { createTypographyHelper } from "../../../utils/typography";

import { useLoading } from "../../../components/LoadingContext";
import { HanedaAirport } from "./components/HanedaAirport";
import { NaritaAirport } from "./components/NaritaAirport";
import { TokyoStation } from "./components/TokyoStation";

import { BookSection } from "./components/BookSection";
import { ShinagawaStation } from "./components/ShinagawaStation";
import { YokohamaOsanbashiPier } from "./components/YokohamaOsanbashiPier";

import { useHistory, useLocation } from "react-router-dom";


const MapBanner = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/maps/banner.jpg";
const BookHanedaAirportBackground = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/maps/book-haneda-background.png";
const BookNaritaAirportBackground = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/maps/book-narita-background.png";
const BookStationBackground = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/maps/book-station-background.png";
const BookPierBackground = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/maps/book-pier-background.png";
const MapPage = () => {
  const { t, i18n, ready } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const getTypographyClass = createTypographyHelper(i18n.language);

  const tabList = useMemo(
    () => [
      {
        id: "haneda-airport",
        bookBackground: BookHanedaAirportBackground,
      },
      {
        id: "narita-airport",
        bookBackground: BookNaritaAirportBackground,
      },
      {
        id: "tokyo-station",
        bookBackground: BookStationBackground,
      },
      {
        id: "shinagawa-station",
        bookBackground: BookStationBackground,
      },
      {
        id: "yokohama-osanbashi-pier",
        bookBackground: BookPierBackground,
      },
    ],
    []
  );

  const defaultTabId = tabList[0]?.id ?? "";
  const [activeTab, setActiveTab] = useState(defaultTabId);

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) {
      return;
    }

    setActiveTab(tabId);
    const params = new URLSearchParams(location.search);
    params.set("type", tabId);
    params.delete("t");
    history.push({ search: `?${params.toString()}` });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const typeParam = searchParams.get("type");
    const matchedTab = tabList.find((tab) => tab.id === typeParam);
    const nextActiveTab = matchedTab ? matchedTab.id : defaultTabId;

    setActiveTab((prev) => (prev === nextActiveTab ? prev : nextActiveTab));
  }, [location.search, tabList, defaultTabId]);

  const activeTabConfig = tabList.find((tab) => tab.id === activeTab) ?? tabList[0];

  const kebabToPascal = (str) => str.replace(/(^|-)(\w)/g, (_, __, letter) => letter.toUpperCase());

  return (
    <FrontLayout>
      <div className="tw:relative tw:max-w-[1920px] tw:mx-auto tw:font-hanken-grotesk tw:mt-[88px]">
        <div className="tw:max-w-[1920px] tw:m-auto tw:relative">
          <div className="tw:bg-[linear-gradient(87.06deg,#000000_6.43%,rgba(40,42,45,0.3)_46.3%)] tw:w-full tw:h-full tw:absolute tw:top-0 tw:left-0"></div>
          <img
            src={MapBanner}
            alt="About Banner"
            className="tw:w-full tw:h-[430px] tw:object-cover"
          />
          <div className="tw:absolute tw:left-5 tw:bottom-8 tw:lg:left-[82px] tw:lg:bottom-[65px] tw:space-y-[14px] tw:z-50">
            <div className="tw:text-[#DBBE78] tw:text-[8px] tw:tracking-[3px] tw:font-semibold tw:font-hanken-grotesk tw:leading-none tw:uppercase">
              {t("Maps")}
            </div>
            <div className="tw:text-[34px] tw:lg:text-[45px] tw:font-libre-caslon-display tw:leading-[58px] tw:text-white tw:font-normal">
              {t("MapPage.Caption")}
            </div>
          </div>
        </div>
        <div className="">
          <div className="tw:bg-[#F9F9F9] tw:relative tw:border-b tw:border-b-[#8D8D8D] tw:h-[72px] tw:lg:h-[100px]">
            <div className="tw:flex tw:lg:px-[82px] tw:lg:justify-start tw:px-3 tw:pt-3 tw:lg:pt-[27px] tw:overflow-x-auto tw:whitespace-nowrap tw:absolute tw:w-full">
              {tabList.map((tab) => (
                <div
                  className={cn(
                    "tw:flex-1 tw:lg:flex-0 tw:text-center tw:flex-shrink-0 tw:h-[60px] tw:lg:h-[73px] tw:px-5 tw:lg:px-[26px] tw:flex tw:items-center tw:justify-center tw:font-hanken-grotesk tw:font-normal tw:lg:font-medium tw:space-x-2 tw:lg:space-x-3",
                    getTypographyClass(13),
                    getTypographyClass(14, "lg"),
                    activeTab === tab.id
                      ? "tw:bg-white tw:border-x tw:border-x-[#8D8D8D] tw:border-t tw:border-t-[#8D8D8D] tw:text-[#282828] tw:rounded-tl-md tw:rounded-tr-md"
                      : "tw:text-[#8D8D8D] tw:cursor-pointer"
                  )}
                  onClick={() => handleTabChange(tab.id)}
                  key={tab.id}
                >
                  <div>{t(`MapPage.${kebabToPascal(tab.id)}.Label`)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {activeTab === "haneda-airport" && <HanedaAirport />}
        {activeTab === "narita-airport" && <NaritaAirport />}
        {activeTab === "tokyo-station" && <TokyoStation />}
        {activeTab === "shinagawa-station" && <ShinagawaStation />}
        {activeTab === "yokohama-osanbashi-pier" && <YokohamaOsanbashiPier />}

        <div className="tw:mt-12 tw:lg:mt-14">
          {!!activeTabConfig && (
            <BookSection
              background={activeTabConfig.bookBackground}
              title={t(`MapPage.${kebabToPascal(activeTab)}.Book.Title`)}
              label={t(`MapPage.${kebabToPascal(activeTab)}.Book.Tag`)}
            />
          )}
        </div>
      </div>
    </FrontLayout>
  );
};

export default MapPage;
